import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import isEqual from 'lodash/isEqual'
import {getIn, useFormikContext} from 'formik'
import {TComponentTextareaOptionsOption, TextareaOptionsSettings} from '../_types'
import {useIntl} from 'react-intl'
import {useAuth} from '../../../../modules/auth'
import api from '../../../../api'
import {getErrorToast, getSuccessToast} from '../../../helpers/toasts'

const mapToOption = (item: {
    title: string
    value: string | number
}): TComponentTextareaOptionsOption => ({label: item.title, value: item.value})

type UseTextareaOptionsArgs = {
    article: string
    settings: TextareaOptionsSettings
    is_visible: boolean
    reloadKey?: any
}


export function useTextareaOptions({
    article,
    settings,
    is_visible,
    reloadKey,
}: UseTextareaOptionsArgs) {
    const {
        object,
        select,
        select_menu,
        list,
        joined_field,
        joined_field_filter,
        rows = 6,
        placeholder,
        label,
        forPrintArticle,
    } = (settings ?? {}) as TextareaOptionsSettings

    const intl = useIntl()
    const {currentUser} = useAuth()
    const {values, setFieldValue, handleSubmit} = useFormikContext<any>()

    // UI
    const [open, setOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [options, setOptions] = useState<TComponentTextareaOptionsOption[]>([])
    const [search, setSearch] = useState('')
    const [paragraphMode, setParagraphMode] = useState(true)
    const [forPrint, setForPrint] = useState(true)
    const [creating, setCreating] = useState(false)
    const [created, setCreated] = useState(false)

    // refs
    const taRef = useRef<HTMLTextAreaElement | null>(null)
    const rootRef = useRef<HTMLDivElement | null>(null)
    const drawerRef = useRef<HTMLElement | null>(null)
    const prevJoinedRef = useRef<any>(null)
    const hasFetchedRef = useRef<boolean>(false)

    // value & errors берёт контейнер через useField, тут нам нужен доступ к formik API

    const forPrintKey = useMemo(
        () => (forPrintArticle ? forPrintArticle : `printFields.${article.replace(/_id$/i, '')}`),
        [forPrintArticle, article]
    )

    useEffect(() => {
        const v = getIn(values, forPrintKey)
        if (typeof v === 'boolean') setForPrint(v)
    }, [values, forPrintKey])

// всегда держим форму в синхронизации с локальным чекбоксом
    useEffect(() => {
        setFieldValue(forPrintKey, forPrint)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forPrint, forPrintKey])

    /** зависимые значения (joined deps) */
    const joinedFieldValue = useMemo(() => {
        if (!joined_field) return null
        const out: Record<string, any> = {}
        if (typeof joined_field === 'string') {
            const key = typeof joined_field_filter === 'string' ? joined_field_filter : joined_field
            const v = (values as any)[joined_field]
            if (v !== null && v !== undefined) out[key] = v
        } else if (Array.isArray(joined_field)) {
            joined_field.forEach((f, i) => {
                const key =
                    Array.isArray(joined_field_filter) && joined_field_filter[i]
                        ? joined_field_filter[i]
                        : f
                const v = (values as any)[f]
                if (v !== null && v !== undefined) out[key] = v
            })
        }
        return out
    }, [values, joined_field, joined_field_filter])

    /** источники */
    const resolvedObject = useMemo(() => object ?? article, [object, article])
    const resolvedSelect = useMemo(() => {
        if (select) return Array.isArray(select) ? select : [select]
        return [article]
    }, [select, article])
    const resolvedSelectMenu = useMemo(() => {
        if (!select_menu) return undefined
        return Array.isArray(select_menu) ? select_menu : [select_menu]
    }, [select_menu])

    /** загрузка опций (лениво, при открытии) */
    const loadOptions = useCallback(async () => {
        if (!is_visible) return

        // локальный список
        if (Array.isArray(list) && list.length) {
            setIsLoading(true)
            try {
                const filtered = joinedFieldValue
                    ? (list as any[]).filter((it) => {
                          for (const k in joinedFieldValue) {
                              if ((joinedFieldValue as any)[k] !== (it as any)[k]) return false
                          }
                          return true
                      })
                    : list
                setOptions(filtered.map(mapToOption))
                prevJoinedRef.current = joinedFieldValue ?? null
            } finally {
                setIsLoading(false)
                hasFetchedRef.current = true
            }
            return
        }

        // объектный источник
        if (!resolvedObject) {
            setOptions([])
            hasFetchedRef.current = true
            setIsLoading(false)
            return
        }

        setIsLoading(true)
        try {
            const baseReq: any = {
                context: {block: 'select'},
                select: ["description"],
                select_menu: resolvedSelectMenu,
            }

            const needDeps = joinedFieldValue && !isEqual(prevJoinedRef.current, joinedFieldValue)
            const req = needDeps ? Object.assign({}, baseReq, joinedFieldValue) : baseReq
            if (needDeps) prevJoinedRef.current = joinedFieldValue

            const {data} = await api<Array<{title: string; value: string | number}>>(
                resolvedObject,
                'get',
                req
            )
            setOptions(data.map(mapToOption))
        } finally {
            setIsLoading(false)
            hasFetchedRef.current = true
        }
    }, [is_visible, list, resolvedObject, resolvedSelect, resolvedSelectMenu, joinedFieldValue])

    /** открытие */
    const openDrawer = useCallback(() => {
        setOpen(true)
        setSearch('')
        if (!hasFetchedRef.current) {
            setOptions([])
            setIsLoading(true)
            loadOptions()
        }
    }, [loadOptions])

    /** закрытие глобальными действиями */
    useEffect(() => {
        const BODY_CLASS = 'textarea-drawer-open'
        if (open) {
            document.body.classList.add(BODY_CLASS)
        } else {
            document.body.classList.remove(BODY_CLASS)
        }
        if (!open) return

        const onEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false)
        }
        const handleDown = (e: MouseEvent) => {
            const t = e.target as Node
            if (rootRef.current?.contains(t)) return
            if (drawerRef.current?.contains(t)) return
            setOpen(false)
        }
        const handleFocus = (e: FocusEvent) => {
            const t = e.target as Node
            if (rootRef.current?.contains(t)) return
            if (drawerRef.current?.contains(t)) return
            setOpen(false)
        }

        document.addEventListener('keydown', onEsc)
        document.addEventListener('mousedown', handleDown)
        document.addEventListener('focusin', handleFocus)
        return () => {
            document.removeEventListener('keydown', onEsc)
            document.removeEventListener('mousedown', handleDown)
            document.removeEventListener('focusin', handleFocus)
        }
    }, [open])

    /** перезагрузка при смене зависимостей */
    useEffect(() => {
        if (!is_visible) return
        prevJoinedRef.current = null
        hasFetchedRef.current = false
        setOptions([])
        if (open) loadOptions()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reloadKey, resolvedObject, JSON.stringify(joinedFieldValue)])

    /** синк «для печати» */
    // useEffect(() => {
    //     if (forPrintArticle) setFieldValue(forPrintArticle, forPrint)
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [forPrint])

    /** вставка по каретке с одним \n максимум */
    const insertAtCursor = (el: HTMLTextAreaElement, text: string) => {
        const start = el.selectionStart ?? el.value.length
        const end = el.selectionEnd ?? el.value.length
        const before = el.value.slice(0, start)
        const after = el.value.slice(end)
        const next = before + text + after
        const caret = start + text.length
        return {next, caret}
    }

    const addText = useCallback(
        (line: string, currentValue: string, articleName: string) => {
            const el = taRef.current
            const hasValue = (currentValue ?? '').length > 0
            const prefix = (paragraphMode && hasValue) || (!paragraphMode && hasValue) ? '\n' : ''
            const insert = prefix + line
            if (el) {
                const {next, caret} = insertAtCursor(el, insert)
                setFieldValue(articleName, next)
                requestAnimationFrame(() => {
                    el.focus()
                    el.setSelectionRange(caret, caret)
                })
            } else {
                setFieldValue(articleName, (currentValue || '') + insert)
            }
        },
        [paragraphMode, setFieldValue]
    )

    /** создать заготовку: complaints/add */
    const createTemplate = useCallback(
        async (val: string | undefined | null) => {
            const text = (val ?? '').trim()
            if (creating) return
            if (!text) {
                getErrorToast(intl.formatMessage({id: 'VALIDATION.FIELD_NULL'}))
                return
            }
            if (!currentUser?.id) {
                getErrorToast(intl.formatMessage({id: 'AUTH.GENERAL.ERROR'}))
                return
            }

            try {
                setCreating(true)
                await api(article, 'add', {
                    description: text,
                    is_general: false,
                    employees: [currentUser.id],
                    ...(joinedFieldValue && Object.keys(joinedFieldValue).length ? joinedFieldValue : {}),
                })
                setCreated(true)
                getSuccessToast(intl.formatMessage({id: 'FIELD.SAVED'}))
                setTimeout(() => setCreated(false), 1200)

                // обновим список
                hasFetchedRef.current = false
                if (open) await loadOptions()
            } catch (e) {
                getErrorToast(intl.formatMessage({id: 'AUTH.SERVER.SERVER_ERROR'}))
            } finally {
                setCreating(false)
            }
        },
        [creating, currentUser?.id, joinedFieldValue, open, loadOptions, intl]
    )

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase()
        if (!q) return options
        return options.filter((o) => (o.label ?? '').toLowerCase().includes(q))
    }, [options, search])

    return {
        // state
        open,
        isLoading,
        options,
        filtered,
        search,
        paragraphMode,
        forPrint,
        creating,
        created,
        // refs
        taRef,
        rootRef,
        drawerRef,
        // meta
        rows,
        placeholder,
        label,
        // handlers
        setSearch,
        setParagraphMode,
        setForPrint,
        openDrawer,
        setOpen,
        addText,
        createTemplate,
        // helpers
        handleSubmit,
    }
}
